<script lang="ts" setup>
import {
  ToccoBaseModuleFieldsFragment,
  ToccoModuleRunFieldsFragment,
  ToccoModuleRunsQueryDocument,
  ToccoModuleRunsQueryQueryVariables,
} from '~/api/graphql/generated'
import {
  isELearningTeachingMethod,
  isRegistrationEnabled,
  isRunRegistrationOpen,
  parseToccoDate,
} from '~/lib/utils/toccoUtils'
import { useTypedAsyncQuery } from '~/lib/utils/graphqlDocument'
import type { ToccoFieldData } from '~/types'

const props = defineProps<{
  entity: ToccoBaseModuleFieldsFragment
}>()

const isELearning = computed(() => isELearningTeachingMethod(props.entity.fieldTeachingMethod))

const { t } = useI18n()
const localePath = useLocalePath()

const variables: ToccoModuleRunsQueryQueryVariables = {
  baseModuleId: (props.entity.id ?? '').toString(),
}
const { data, error } = await useTypedAsyncQuery(ToccoModuleRunsQueryDocument, variables)

const rawRunEntities = computed(() => data.value?.toccoQuery?.entities ?? null)

// C1: the entities query is published-only, so an empty array cannot prove
// that no runs exist. The status-free aliased count is the authoritative
// total; anything but an exact 0 (null/missing/nonzero) blocks the fallback.
const allRunsTotalCount = computed(() => data.value?.allRunsCount?.count ?? null)

const allRuns = computed(() =>
  (rawRunEntities.value ?? []).filter(Boolean).map((run) => run as ToccoModuleRunFieldsFragment),
)

const runs = computed(() => allRuns.value.filter((run) => isRunRegistrationOpen(run)))

const selectedRunId = ref((runs.value[0]?.id ?? '').toString())

// CARW-1703: e-learnings without any module runs register on the base module
// itself. The fallback needs positive evidence: a successful runs query with
// zero TOTAL runs — an e-learning whose runs are merely closed/not yet open
// keeps the empty state (never bypass a run's registration window), and a
// failed query is not evidence of "no runs".
const fieldToccoDataJSON = computed<ToccoFieldData | null>(() => {
  if (!props.entity.fieldToccoData) return null
  try {
    return JSON.parse(props.entity.fieldToccoData)
  } catch {
    return null
  }
})

const isUsableWindowValue = (value?: string | null) => !value || !Number.isNaN(parseToccoDate(value).getTime())

const hasBaseModuleRegistration = computed(() => {
  if (!isELearning.value || error.value) return false
  // Positive zero-run evidence only: the raw entities array must exist and be
  // truly empty. Nullable GraphQL shapes ({toccoQuery: null}, entities: null)
  // and unreadable [null] entries are unknowns, not proof of "no runs".
  if (!Array.isArray(rawRunEntities.value) || rawRunEntities.value.length !== 0) return false
  if (allRunsTotalCount.value !== 0) return false
  if (!props.entity.fieldExternalId?.trim()) return false
  const toccoData = fieldToccoDataJSON.value
  if (!toccoData?.paths) return false
  // isRegistrationEnabled fails open on unparseable timestamps; this fallback
  // must not open base-module registration on a malformed window.
  const from = toccoData.paths.registration_internet_from?.value
  const till = toccoData.paths.registration_internet_till?.value
  if (!isUsableWindowValue(from) || !isUsableWindowValue(till)) return false
  return isRegistrationEnabled(toccoData)
})

const registrationLink = computed(() => {
  if (hasBaseModuleRegistration.value) return localePath(`/registration/module/${props.entity.id}`)
  return selectedRunId.value ? localePath(`/registration/module/${selectedRunId.value}`) : ''
})
</script>

<template>
  <SectionSpecial :title="t('tocco.runSelection.title')" data-id="dates">
    <div v-if="runs.length || hasBaseModuleRegistration" class="runs-wrapper">
      <div v-animate="{ animation: 'vertical-reveal' }" class="runs">
        <CourseRunRow
          v-for="run in runs"
          :key="run.id ?? ''"
          :run="run"
          :name="`course-run-${entity.id}`"
          :e-learning="isELearning"
          :selected="selectedRunId === (run.id ?? '').toString()"
          @select="selectedRunId = (run.id ?? '').toString()"
        />
        <CourseRunRow
          v-if="hasBaseModuleRegistration"
          :name="`course-run-${entity.id}`"
          :e-learning="true"
          :selected="true"
        />
      </div>
      <Button primary slide :to="registrationLink" class="register-button">
        {{ t('tocco.runSelection.cta') }}
      </Button>
    </div>
    <p v-else v-animate="{ animation: 'vertical-reveal' }" class="empty-state">
      {{ t('tocco.runSelection.emptyState') }}
    </p>
  </SectionSpecial>
</template>

<style lang="scss" scoped>
// The band heading must fit "Termine und Anmeldung" (CARW-1705) on one
// line at 44px; the artboard reserves ~702px (~6 columns), the shared
// default (ds-4) only ~451px. Scoped here — SectionSpecial's default stays.
:deep(.header .text) {
  @include breakpoint(ds) {
    --span: 6;
  }
}

.runs {
  border: 1px solid var(--c-line-light);
  padding: 0 24px;

  background: var(--c-white);
}

.register-button {
  margin-top: 32px;
}

.empty-state {
  margin-bottom: 0;
}
</style>
